import { apiService } from './api';
import api from './api';
import { User, UserRole, Pagination, ApiResponse } from '../types';

export interface BulkCreatedUser {
  _id: string;
  name: string;
  userCode?: string;
}

export interface BulkCreateResult {
  created: number;
  users: BulkCreatedUser[];
}

export const userService = {
  // Get all users with pagination and filters
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string; // Changed from UserRole to string to support role IDs from database
    centerId?: string;
    isActive?: boolean;
  }) => {
    const response = await apiService.get<{
      users: User[];
      pagination: Pagination;
    }>('/users', params);
    return response.data;
  },

  // Get single user by ID
  getUser: async (id: string) => {
    const response = await apiService.get<User>(`/users/${id}`);
    return response.data;
  },

  // Create new user
  createUser: async (data: {
    name: string;
    email: string;
    password?: string;
    role: UserRole;
    centerId?: string;
  }) => {
    const response = await apiService.post<User>('/users', data);
    return response.data;
  },

  // Update user
  updateUser: async (id: string, data: {
    name?: string;
    email?: string;
    role?: UserRole;
    centerId?: string;
    isActive?: boolean;
  }) => {
    const response = await apiService.put<User>(`/users/${id}`, data);
    return response.data;
  },

  // Toggle employee active/inactive status (dedicated endpoint)
  updateUserStatus: async (id: string, isActive: boolean) => {
    const response = await apiService.patch<User>(`/users/${id}/status`, { isActive });
    return response.data;
  },

  // Delete user
  deleteUser: async (id: string) => {
    const response = await apiService.delete(`/users/${id}`);
    return response.data;
  },

  // Reset user password
  resetPassword: async (id: string) => {
    const response = await apiService.post<{ temporaryPassword: string }>(`/users/${id}/reset-password`);
    return response.data;
  },

  // Reset user password with new password (for admins)
  resetUserPassword: async (id: string, newPassword?: string) => {
    const response = await apiService.post(`/users/${id}/reset-password`, newPassword ? { newPassword } : {});
    return response.data;
  },

  // Get users by center
  getUsersByCenter: async (centerId: string) => {
    const response = await apiService.get<User[]>(`/users/center/${centerId}`);
    return response.data;
  },

  // Get users by role
  getUsersByRole: async (role: UserRole, centerId?: string) => {
    const params = centerId ? { centerId } : {};
    const response = await apiService.get<User[]>(`/users/role/${role}`, params);
    return response.data;
  },

  // Get user statistics
  getStats: async (centerId?: string) => {
    const params = centerId ? { centerId } : {};
    const response = await apiService.get<{
      total: number;
      active: number;
      inactive: number;
      byRole: { [key: string]: number };
      byCenter: { [key: string]: number };
    }>('/users/stats', params);
    return response.data;
  },

  // Get roles for filtering (from user's school)
  getRoles: async (schoolId?: string) => {
    const params = schoolId ? { schoolId } : {};
    const response = await apiService.get<Array<{ _id: string; name: string; description?: string }>>('/users/roles', params);
    return response.data;
  },

  // Bulk create employees — one role, one shared password, list of names.
  // Server generates userCodes and issues a single insertMany.
  bulkCreate: async (payload: { names: string[]; roleId: string; password: string }): Promise<BulkCreateResult> => {
    const response = await apiService.post<BulkCreateResult>('/users/bulk-create', payload);
    if (!response.success) throw new Error(response.message);
    return response.data as BulkCreateResult;
  },

  // One-column ('Name') sample CSV — triggers a browser download.
  downloadBulkSampleCSV: async (): Promise<void> => {
    const res = await api.get('/users/bulk-sample-csv', { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employees_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};